import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import LazyRoute from "@shared/components/loading/LazyRoute";
import { WorkspaceLoading } from "@screens/workspace/WorkspaceLoading";
import { ProtectedRoute } from "@screens/auth/ProtectedRoute";
import { I18nProvider } from "@shared/i18n/I18nProvider";
import { GoogleCallback } from "@screens/login/GoogleCallback";
import { AuthListener } from "@screens/auth/AuthListener";
import { ToastProvider } from "@shared/components/ui/ToastContext";
import { ToastContainer } from "@shared/components/ui/ToastContainer";
import { useErrorHandler } from "@shared/hooks/useErrorHandler";
import { LEGACY_CLASS_PATTERN, LEGACY_ROUTES, ROUTES } from "@shared/routes";
import { LegacyClassRedirect } from "@shared/components/routing/LegacyClassRedirect";

const DesignSystem = import.meta.env.DEV
  ? lazy(() => import("@screens/designSystem/DesignSystem"))
  : null;

function AppContent() {
  useErrorHandler();

  return (
    <>
      <ToastContainer />
      <Routes>
        <Route
          path={ROUTES.login}
          element={<LazyRoute componentPath="auth/Login" />}
        />
        <Route path={ROUTES.authCallback} element={<GoogleCallback />} />

        <Route
          element={
            <ProtectedRoute>
              <LazyRoute componentPath="workspace/WorkspaceLayout" fallback={<WorkspaceLoading />} />
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.home} element={<LazyRoute componentPath="workspace/Dashboard" />} />
          <Route path={ROUTES.storage} element={<LazyRoute componentPath="workspace/Storage" />} />
          <Route path={ROUTES.agenda} element={<LazyRoute componentPath="workspace/Agenda" />} />
          <Route path={ROUTES.tasks} element={<LazyRoute componentPath="workspace/Tasks" />} />
          <Route path={ROUTES.settings} element={<LazyRoute componentPath="workspace/Settings" />} />
        </Route>

        <Route element={<LazyRoute componentPath="workspace/WorkspaceLayout" fallback={<WorkspaceLoading />} />}>
          <Route path={ROUTES.classPattern} element={<LazyRoute componentPath="workspace/ClassPage" />} />
        </Route>

        {DesignSystem && (
          <Route
            path={ROUTES.design}
            element={
              <Suspense fallback={null}>
                <DesignSystem />
              </Suspense>
            }
          />
        )}

        {LEGACY_ROUTES.map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        <Route path={LEGACY_CLASS_PATTERN} element={<LegacyClassRedirect />} />

        <Route path="/" element={<Navigate to={ROUTES.home} replace />} />
        <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <I18nProvider>
      <ToastProvider>
        <AuthListener>
          <AppContent />
        </AuthListener>
      </ToastProvider>
    </I18nProvider>
  );
}

export default App;
